import { mergeAnswers } from "../../../../questionnaire/utils/mergeAnswers";
import type {
  AddressSyncConfig,
  QuestionnaireState,
  UpdateAnswerPayload,
} from "../../../types";
import type { QuestionProps } from "../../../../../components/Questionnaire/Section/Question/types";
import { applyGroupAnswerAndVisibility } from "./applyGroupAnswerAndVisibility";

export const handleUpdateAnswerAction = (
  state: QuestionnaireState,
  payload: UpdateAnswerPayload,
): QuestionnaireState => {
  const { answers, changedQuestions, isEditing, answerTimestamps = {} } = state;
  // If the answer hasn't changed, return the current state
  if (payload.value === answers[payload.name]) return state;

  const addressSyncConfig = state.config?.addressSync as
    | AddressSyncConfig
    | undefined;
  const mergedAnswers = mergeAnswers(answers, payload, addressSyncConfig);
  const updatedChangedQuestions = [...(changedQuestions ?? []), payload.name];

  // Record timestamp for this answer update
  const updatedTimestamps = {
    ...answerTimestamps,
    [payload.name]: Date.now(),
  };

  const updatedState = {
    ...state,
    answers: mergedAnswers,
    answerTimestamps: updatedTimestamps,
    changedQuestions: isEditing ? updatedChangedQuestions : changedQuestions,
  };

  // Build a map of server-provided visibility for each question
  // In stacked-sections mode, all sections are visible so we need visibility from ALL sections
  // In standard mode, only the current section is visible
  const isStackedMode = state.mode === "stacked-sections";
  const relevantQuestions = isStackedMode
    ? state.sections.flatMap(
        section => (section.questions as QuestionProps[]) ?? [],
      )
    : ((state.sections[state.currentSectionIndex]
        ?.questions as QuestionProps[]) ?? []);

  const serverVisibleState = Object.fromEntries(
    relevantQuestions.map(({ name, visible }) => [name, Boolean(visible)]),
  ) as Record<string, boolean>;

  const { answers: groupAnswers, visibility: groupVisibility } =
    applyGroupAnswerAndVisibility(updatedState, payload, serverVisibleState);

  return {
    ...updatedState,
    answers: groupAnswers,
    visibility: groupVisibility,
  };
};
